import { forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useDropzone } from 'react-dropzone';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { color } from '@/colors';
import { themeColor } from '@/UI/themeColors';
import { I18N_KEYS } from '@/language/keys';
import { FlexCenVer, FlexCol } from '@/UI/styles';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { AppIconButton } from '@/UI/Buttons/AppIconButton';
import { ImageIcon } from '@/UI/Icons/ImageIcon';
import { XIcon } from '@/UI/Icons/XIcon';
import { useIsHovered } from '@/hooks/useIsHovered';
import { COMMUNITY_PUBLISH_IMAGE_TYPES } from '@/utils/fileTypes';

export interface PublishImageDropzoneHandle {
  open: () => void;
}

/**
 * Narrower than this and "Upload image" no longer fits on one line, so the hover overlay
 * drops to the short label at the smaller button size. The 90px icon slot is the case in point.
 */
const COMPACT_OVERLAY_MAX_WIDTH = 120;

interface PublishImageDropzoneProps {
  width: number | string;
  height: number;
  /** Set it to point a `<label htmlFor>` at the hidden file input. */
  inputId?: string;
  previewUrl: string;
  onFile: (file: File) => void;
  onRemove?: () => void;
  hasError?: boolean;
  suppressHoverOverlay?: boolean;
}

const PublishImageDropzone = forwardRef<PublishImageDropzoneHandle, PublishImageDropzoneProps>(
  ({ width, height, inputId, previewUrl, onFile, onRemove, hasError = false, suppressHoverOverlay = false }, ref) => {
    const { t } = useTranslation();
    const { isHovered, onMouseEnter, onMouseLeave } = useIsHovered();
    const hasImage = Boolean(previewUrl);
    const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
      accept: COMMUNITY_PUBLISH_IMAGE_TYPES,
      multiple: false,
      onDrop: (acceptedFiles) => {
        const file = acceptedFiles[0];
        if (file) onFile(file);
      },
    });

    useImperativeHandle(ref, () => ({ open }), [open]);

    const showError = hasError && !hasImage;
    const radius = hasImage || showError ? '7px' : '4px';
    const showHoverOverlay = isHovered && !suppressHoverOverlay;
    const isCompactOverlay = typeof width === 'number' && width < COMPACT_OVERLAY_MAX_WIDTH;

    let background: string;
    let border: string;
    if (isDragActive) {
      background = themeColor.white08_T;
      border = `1px solid ${themeColor.white100}`;
    } else if (hasImage) {
      background = token.bgTransparent;
      border = `1px solid ${token.border}`;
    } else if (showError) {
      background = color.Weavy_Error_08_T;
      border = `1px dashed ${color.Weavy_Error_40_T}`;
    } else {
      background = token.bgTransparent;
      border = `1px dashed ${color.White08_T}`;
    }

    return (
      <FlexCol
        {...getRootProps()}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        sx={{
          position: 'relative',
          width,
          height,
          flexShrink: 0,
          alignItems: 'center',
          justifyContent: 'center',
          background,
          border,
          borderRadius: radius,
          overflow: 'visible',
        }}
      >
        <input {...getInputProps({ id: inputId })} />
        {hasImage ? (
          <img
            src={previewUrl}
            alt=""
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: '7px',
            }}
          />
        ) : (
          <ImageIcon
            width={24}
            height={24}
            style={{ color: showError ? color.Weavy_Error_64_T : token.textSecondary }}
          />
        )}
        {showHoverOverlay && (
          <FlexCenVer
            sx={{
              position: 'absolute',
              inset: 0,
              background: color.Black64_T,
              borderRadius: radius,
              justifyContent: 'center',
            }}
          >
            <FlexCenVer sx={{ maxWidth: 'calc(100% - 16px)', minWidth: 0 }}>
              <ButtonContained
                mode="filled-light"
                size={isCompactOverlay ? 'xs' : 'small'}
                onClick={(e) => {
                  e.stopPropagation();
                  open();
                }}
                sx={{
                  maxWidth: '100%',
                  minWidth: 0,
                  px: isCompactOverlay ? '10px' : undefined,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  background: themeColor.white64,
                  color: color.Black100,
                  '&:hover': { background: themeColor.white100 },
                }}
              >
                {t(
                  isCompactOverlay
                    ? I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.IMAGES_STEP.UPLOAD
                    : I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.IMAGES_STEP.UPLOAD_IMAGE,
                )}
              </ButtonContained>
            </FlexCenVer>
          </FlexCenVer>
        )}
        {hasImage && showHoverOverlay && onRemove && (
          <AppIconButton
            aria-label={t(I18N_KEYS.GENERAL.ARIA.REMOVE_IMAGE)}
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            sx={{
              position: 'absolute',
              top: -8,
              right: -9,
              width: 24,
              height: 24,
              background: token.bg,
              borderRadius: '16px',
              boxShadow:
                '0px 0px 0.75px rgba(0, 0, 0, 0.12), 0px 15px 24px rgba(0, 0, 0, 0.12), 0px 3px 7.5px rgba(0, 0, 0, 0.15)',
              '&:hover': { background: token.bg },
            }}
          >
            <XIcon width={18} height={18} />
          </AppIconButton>
        )}
      </FlexCol>
    );
  },
);

PublishImageDropzone.displayName = 'PublishImageDropzone';

export default PublishImageDropzone;
